import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi, uploadFileToSignedUrl } from '../lib/api.ts';
import { useConfirm } from '../lib/confirm.tsx';
import { Btn } from '../components/Btn.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextArea, TextInput } from '../components/Field.tsx';
import { FileDrop } from '../components/FileDrop.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import type { TrainingModule, TrainingModuleAudience } from '../lib/types.ts';

const AUDIENCE_LABELS: Record<TrainingModuleAudience, string> = {
  both: 'Companies & drivers',
  company: 'Companies',
  driver: 'Drivers',
};

export function AdminTraining() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { data, isLoading } = useQuery({
    queryKey: ['training'],
    queryFn: () => adminApi.get<{ training_modules: TrainingModule[] }>('/api/training'),
  });

  const [editing, setEditing] = useState<TrainingModule | null>(null);
  const [uploadingHtml, setUploadingHtml] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.delete(`/api/training/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['training'] }),
  });

  if (isLoading) return <CenteredSpinner />;
  const modules = data?.training_modules ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Training</h1>
          <p className="mt-1 text-sm text-slate-500">
            Upload standalone HTML training modules with a pass mark. An active module is
            automatically assigned to everyone in its audience and appears on their Training
            tab; reaching the pass mark completes it.
          </p>
        </div>
        <Btn onClick={() => setUploadingHtml(true)}>Upload HTML module</Btn>
      </div>

      {modules.length === 0 ? (
        <EmptyState
          title="No training modules yet"
          description="Upload an HTML module to get started."
          action={<Btn onClick={() => setUploadingHtml(true)}>Upload HTML module</Btn>}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {modules.map((m) => (
            <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{m.title}</span>
                    {m.audience !== 'both' && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${
                          m.audience === 'driver'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {AUDIENCE_LABELS[m.audience]}
                      </span>
                    )}
                    {!m.is_active && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        inactive
                      </span>
                    )}
                  </div>
                  {m.description && (
                    <div className="mt-0.5 text-xs text-slate-500">{m.description}</div>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    className="text-xs text-slate-600 hover:underline"
                    onClick={() => setEditing(m)}
                  >
                    Edit
                  </button>
                  <button
                    className="text-xs text-slate-600 hover:underline"
                    onClick={() => {
                      void (async () => {
                        const res = await adminApi
                          .post<{ url: string }>(`/api/training/${m.id}/preview-launch`, {})
                          .catch(() => null);
                        if (res?.url) window.open(res.url, '_blank', 'noopener');
                      })();
                    }}
                  >
                    Preview
                  </button>
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => {
                      void (async () => {
                        if (
                          await confirm({
                            title: 'Delete training module',
                            body: (
                              <>
                                Delete <span className="font-semibold">{m.title}</span>?
                              </>
                            ),
                            confirmLabel: 'Delete',
                            danger: true,
                          })
                        )
                          deleteMutation.mutate(m.id);
                      })();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Pass mark {m.pass_threshold}%
                {m.html_file_name ? ` · from ${m.html_file_name}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteMutation.error && <ErrorMessage error={deleteMutation.error} />}

      {editing && <TrainingEditor existing={editing} onClose={() => setEditing(null)} />}
      {uploadingHtml && (
        <HtmlUploadModal
          onClose={() => setUploadingHtml(false)}
          onUploaded={(m) => {
            setUploadingHtml(false);
            setEditing(m);
          }}
        />
      )}
    </div>
  );
}

function AudienceSelect({
  value,
  onChange,
}: {
  value: TrainingModuleAudience;
  onChange: (a: TrainingModuleAudience) => void;
}) {
  return (
    <Field
      label="Audience"
      hint="Who this training is assigned to — everyone in this audience sees it on their Training tab."
    >
      <Select value={value} onChange={(e) => onChange(e.target.value as TrainingModuleAudience)}>
        {(Object.keys(AUDIENCE_LABELS) as TrainingModuleAudience[]).map((a) => (
          <option key={a} value={a}>
            {AUDIENCE_LABELS[a]}
          </option>
        ))}
      </Select>
    </Field>
  );
}

function TrainingEditor({ existing, onClose }: { existing: TrainingModule; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(existing.title);
  const [description, setDescription] = useState(existing.description ?? '');
  const [isActive, setIsActive] = useState(existing.is_active);
  const [passThreshold, setPassThreshold] = useState(existing.pass_threshold);
  const [audience, setAudience] = useState<TrainingModuleAudience>(existing.audience);
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: () =>
      adminApi.patch(`/api/training/${existing.id}`, {
        title: title.trim(),
        description: description.trim() || null,
        is_active: isActive,
        audience,
        pass_threshold: passThreshold,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['training'] });
      onClose();
    },
    onError: setError,
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit ${existing.title}`}
      size="lg"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose}>
            Cancel
          </Btn>
          <Btn onClick={() => save.mutate()} loading={save.isPending} disabled={!title.trim()}>
            Save
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
          Hosted HTML module: {existing.html_file_name}
        </div>
        <Field label="Title" required>
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Description">
          <TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <AudienceSelect value={audience} onChange={setAudience} />
        <Field label="Pass mark (%)">
          <TextInput
            type="number"
            min={0}
            max={100}
            value={String(passThreshold)}
            onChange={(e) =>
              setPassThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
            }
          />
        </Field>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Active
        </label>
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}

function HtmlUploadModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: (m: TrainingModule) => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [passThreshold, setPassThreshold] = useState(80);
  const [audience, setAudience] = useState<TrainingModuleAudience>('both');
  const [step, setStep] = useState<'idle' | 'uploading' | 'registering'>('idle');
  const [error, setError] = useState<unknown>(null);

  async function submit() {
    if (!file) return;
    setError(null);
    try {
      setStep('uploading');
      const presigned = await adminApi.post<{ path: string; url: string }>(
        '/api/training/upload-url',
        { file_name: file.name, mime_type: 'text/html' },
      );
      await uploadFileToSignedUrl(presigned.url, file);
      setStep('registering');
      const res = await adminApi.post<{ training_module: TrainingModule }>('/api/training/html', {
        title: title.trim() || file.name.replace(/\.[^.]+$/, ''),
        html_path: presigned.path,
        html_file_name: file.name,
        pass_threshold: passThreshold,
        audience,
      });
      await qc.invalidateQueries({ queryKey: ['training'] });
      onUploaded(res.training_module);
    } catch (err) {
      setError(err);
    } finally {
      setStep('idle');
    }
  }

  return (
    <Modal
      open
      onClose={() => step === 'idle' && onClose()}
      title="Upload HTML training module"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={step !== 'idle'}>
            Cancel
          </Btn>
          <Btn onClick={submit} loading={step !== 'idle'} disabled={!file}>
            Upload
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        <FileDrop
          multiple={false}
          accept=".html,text/html"
          onFiles={(files) => {
            const f = files.item(0);
            if (f) {
              setFile(f);
              setTitle((t) => t || f.name.replace(/\.[^.]+$/, ''));
            }
          }}
          hint="Standalone training module (.html)"
        />
        {file && (
          <div className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {file.name} ({Math.round(file.size / 1024)} KB)
          </div>
        )}
        <Field label="Module title">
          <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Pass mark (%)">
          <TextInput
            type="number"
            min={0}
            max={100}
            value={String(passThreshold)}
            onChange={(e) =>
              setPassThreshold(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
            }
          />
        </Field>
        <AudienceSelect value={audience} onChange={setAudience} />
        <p className="text-xs text-slate-500">
          Trainees open the module full-screen from their Training tab; their quiz score is
          reported back and the module completes when it meets the pass mark.
        </p>
        {step !== 'idle' && <div className="text-xs text-slate-500">Working…</div>}
        <ErrorMessage error={error} />
      </div>
    </Modal>
  );
}
